import { useLocation } from "@reach/router";
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { validateToken } from "../services/auth";

interface UserProfile {
  firstName: string;
  lastName: string;
  email: string;
}

interface UserContextType {
  isSignedIn: boolean | null;
  userProfile: UserProfile | null;
  setUserProfile: (profile: UserProfile) => void;
  refreshSignedIn: () => void;
}

interface UserProviderProps {
  children: ReactNode;
}

const Context = createContext<UserContextType>({
  isSignedIn: null,
  userProfile: null,
  setUserProfile: () => { },
  refreshSignedIn: () => { },
});

export const UserProvider = ({ children }: UserProviderProps): ReactNode => {
  const [isSignedIn, setIsSignedIn] = useState<boolean | null>(null);
  const [userProfile, setUserProfileState] = useState<UserProfile | null>(null);
  const location = useLocation(); // Get the current pathname

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedUser = localStorage.getItem("userProfile");
      if (storedUser) {
        setUserProfileState(JSON.parse(storedUser));
      }
    }
  }, []);

  const refreshSignedIn = useCallback(async () => {
    let isMounted = true;
    console.log("Refreshing signed in status");

    try {
      const { valid } = await validateToken();
      if (isMounted) {
        setIsSignedIn(valid);
        console.log("Signed in status:", valid);

        if (!valid) {
          setUserProfile(null);
          localStorage.removeItem("userProfile");
        }
      }
    } catch (error) {
      console.error("Error validating token:", error);
      if (isMounted) {
        setIsSignedIn(false);
        setUserProfile(null);
        localStorage.removeItem("userProfile");
      }
    }

    return () => {
      isMounted = false; // Cleanup
    };
  }, []);

  const setUserProfile = (user: UserProfile | null) => {
    setUserProfileState(user);
    if (user) {
      localStorage.setItem("userProfile", JSON.stringify(user));
    } else {
      localStorage.removeItem("userProfile");
    }
  };

  console.log(">>> isSignedIn", isSignedIn);

  useEffect(() => {
    refreshSignedIn();
  }, [location.pathname, refreshSignedIn]);

  return (
    <Context.Provider value={{ isSignedIn, userProfile, setUserProfile, refreshSignedIn }}>
      {children}
    </Context.Provider>
  );
};

export const useUser = () => {
  const context = useContext(Context);
  if (!context) {
    throw new Error("useUser must be used within an UserProvider");
  }
  return context;
};